import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Award, Heart, Shield, Target } from 'lucide-react';
import { companyInfo } from '../data/mockData';

const About = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-[#262626] mb-6">
                About EC Home Builds
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-6">
                Founded and led by <span className="font-semibold text-[#DAA520]">Erick Cordero</span>, EC Home Builds is dedicated to transforming the custom home building experience in Central Ohio.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With over 11 years of hands-on experience in construction and real estate development, we understand that building a home is much more than just laying foundations and framing walls. It's about creating lasting value and building equity for both families and investors.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1581674662583-5e89b374fae6?q=85&w=2000"
                alt="Professional Construction Planning"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              To help our clients build wealth through quality homes designed to stand the test of time. Every project reflects our dedication to <span className="font-semibold text-[#DAA520]">precision, integrity, and excellence</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Three pillars that define everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: 'Exceptional Craftsmanship',
                description: 'Over 11 years of hands-on experience ensuring every detail is executed with precision. We use premium materials and proven techniques to create homes built to last generations.'
              },
              {
                icon: Shield,
                title: 'Transparent Project Management',
                description: 'Fixed-price contracts, detailed timelines, and regular updates. You\'ll never be left wondering about costs, progress, or next steps. Clear communication, always.'
              },
              {
                icon: Heart,
                title: 'Unwavering Client Commitment',
                description: 'Your vision drives every decision. We\'re not satisfied until you\'re thrilled with your home. Our success is measured by your satisfaction and long-term happiness.'
              }
            ].map((value, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-[#DAA520]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <value.icon className="text-[#DAA520]" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#262626] mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Erick's Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <img
                src="https://customer-assets.emergentagent.com/job_luxury-homes-ohio/artifacts/18o4nej1_IMG_9079.JPG"
                alt="Erick Cordero - Founder of EC Home Builds"
                className="w-48 h-48 rounded-full mx-auto mb-6 object-cover shadow-2xl ring-4 ring-[#DAA520]"
              />
              <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
                Meet Erick Cordero
              </h2>
              <p className="text-xl text-gray-600">
                Founder & Builder
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 md:p-12">
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Erick Cordero brings over <strong className="text-[#DAA520]">11 years of comprehensive experience</strong> in construction and real estate development to every EC Home Builds project. His journey began on job sites, learning the craft from the ground up, and evolved into expertise in project management, real estate investment, and custom home development.
                </p>
                <p>
                  As both a <strong>builder and real estate developer</strong>, Erick understands construction from multiple perspectives. He knows what it takes to create a quality home, what makes a property a smart investment, and how to navigate the complexities of building on your own lot.
                </p>
                <p>
                  This unique combination of hands-on construction expertise and investment acumen allows Erick to guide clients through every aspect of their build—from evaluating lot feasibility to selecting finishes that maximize both livability and resale value.
                </p>
                <p>
                  What sets Erick apart is his commitment to building relationships as solid as the foundations he pours. He founded EC Home Builds on the principle that <strong className="text-[#DAA520]">transparency, quality, and client success</strong> should never be compromised. Every client receives his personal attention, ensuring their vision becomes reality without surprises or shortcuts.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mt-12">
                {[
                  { icon: Award, label: '11+ Years', sublabel: 'Experience' },
                  { icon: Target, label: '50+ Homes', sublabel: 'Built' },
                  { icon: Shield, label: '100%', sublabel: 'Client Satisfaction' }
                ].map((stat, index) => (
                  <div key={index} className="text-center p-6 bg-white rounded-lg">
                    <stat.icon className="text-[#DAA520] mx-auto mb-3" size={32} />
                    <div className="text-3xl font-bold text-[#262626] mb-1">{stat.label}</div>
                    <div className="text-gray-600">{stat.sublabel}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              The EC Home Builds Difference
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              What sets us apart in Central Ohio's custom home building market
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Experience You Can Trust',
                description: 'Over 11 years of hands-on construction and development expertise across every aspect of residential building. From site evaluation to final walkthrough, we\'ve done it all—and we do it right.'
              },
              {
                title: 'Quality Without Compromise',
                description: 'Premium materials, skilled craftsmen, and rigorous quality control at every phase ensure homes built to last. We never cut corners, even when no one\'s watching.'
              },
              {
                title: 'Transparency Always',
                description: 'Clear communication, detailed contracts, regular updates, and honest answers. Fixed-price contracts mean no surprises. You\'ll never be left wondering about costs or timeline.'
              },
              {
                title: 'Investment Focus',
                description: 'We build with equity in mind. Every design decision and material choice considers both your immediate needs and long-term value. Most clients gain 10-20% equity upon completion.'
              },
              {
                title: 'Proven Systems',
                description: 'Our six-phase process has been refined over hundreds of projects. We know what works, what doesn\'t, and how to navigate challenges before they become problems.'
              },
              {
                title: 'Central Ohio Expertise',
                description: 'Deep knowledge of local markets, zoning regulations, and subcontractor relationships. This isn\'t our first project in Columbus—we know this market inside and out.'
              }
            ].map((item, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-[#262626] mb-4">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#262626] mb-6">
              Serving Central Ohio
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Based in Columbus, Ohio, we build throughout the Central Ohio region. Our deep knowledge of local markets, regulations, and subcontractors ensures smooth projects and optimal results.
            </p>
            <p className="text-lg text-gray-700">
              <strong>Primary Service Area:</strong> Columbus, Delaware, Westerville, Dublin, Powell, Lewis Center, and surrounding communities
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#DAA520]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Build With Us?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Experience the EC Home Builds difference. Schedule a consultation to discuss your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#262626] hover:bg-black text-white px-8 py-6 text-lg">
                Schedule Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/floor-plans">
              <Button variant="outline" className="bg-white/10 backdrop-blur-md border-white text-white hover:bg-white hover:text-[#262626] px-8 py-6 text-lg">
                Browse Floor Plans
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
